import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection, useShowsForTours } from '../lib/firestore'
import { useArtists, usePeople } from '../lib/artists'
import { formatDayLong } from '../lib/format'
import type { Artist, Show, Tour, TourCrew, WithId } from '../types/models'
import Icon from './Icon'
import Empty from './Empty'

/* ============================================================================
   The roster timeline.

   Rows are artists, columns are days. Answerable at a glance: who is on the
   road today, who is off, and who has nothing booked at all.

   Deliberately not a calendar. A calendar answers "what is on this date"; an
   agency needs "where is everyone", which is the same data rotated ninety
   degrees. Reading down a column tells you today; reading across a row tells
   you one artist's year.
   ============================================================================ */

const DAY = 86_400_000
/** Width of the artist-name column. Long band names need the room. */
const NAME_W = 208

const RANGES = [
  { days: 28, label: '4 weeks' },
  { days: 90, label: 'Quarter' },
  { days: 180, label: 'Half year' },
] as const

interface Conflict {
  id: string
  severity: 'high' | 'medium'
  title: string
  detail: string
  to?: string
}

/**
 * The roster timeline: artists down, days across.
 *
 * This used to be a page of its own. It now lives as one view of the agency
 * dashboard's itinerary, next to the list and the calendar, because "what is
 * everyone doing" is the same question the itinerary answers, just drawn
 * differently. The component owns its own range control and conflict list, so
 * dropping it into a section costs the host nothing.
 */
export default function RosterTimeline() {
  const { user, uid } = useAuth()
  const { roster: artists, orgId } = useArtists()
  const people = usePeople(orgId)
  const tours = useCollection<Tour>('tours', user ? [where('ownerId', '==', uid)] : [])
  const shows = useShowsForTours<Show>(
    useMemo(() => tours.data.map((tour) => tour.id), [tours.data]),
  )
  const crew = useCollection<TourCrew>('tourCrew', [])

  const [days, setDays] = useState<number>(28)

  // Anchored to midnight and memoised on the date, not the instant — a fresh
  // Date() every render would make every memo below recompute.
  const startMs = useMemo(() => {
    const midnight = new Date()
    midnight.setHours(0, 0, 0, 0)
    return midnight.getTime()
  }, [])

  const columns = useMemo(
    () => Array.from({ length: days }, (_, index) => new Date(startMs + index * DAY)),
    [startMs, days],
  )

  const rows = useMemo(() => {
    const rosterTours = tours.data.filter((tour) => tour.artistId)
    return artists.map((artist) => ({
      artist,
      tours: rosterTours.filter((tour) => tour.artistId === artist.id),
      shows: shows.data.filter((show) =>
        rosterTours.some((tour) => tour.id === show.tourId && tour.artistId === artist.id),
      ),
    }))
  }, [artists, tours.data, shows.data])

  const conflicts = useConflicts({ rows, crew: crew.data, people, startMs, days })

  if (artists.length === 0) {
    return (
      <Empty
        title="No artists yet"
        message="The timeline needs a roster. Add your artists and their tours will line up here."
      />
    )
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {RANGES.map((range) => {
          const on = days === range.days
          return (
            <button
              key={range.days}
              type="button"
              className="chip"
              aria-pressed={on}
              onClick={() => setDays(range.days)}
              style={{
                cursor: 'pointer',
                background: on ? 'var(--accent-wash)' : 'transparent',
                border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
                color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
              }}
            >
              {range.label}
            </button>
          )
        })}
      </div>

      {/* Conflicts are a persistent panel, never a modal. They're a working
          list you fix over a morning, not an interruption. */}
      {conflicts.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <h3 className="t-eyebrow">
            {conflicts.length} thing{conflicts.length === 1 ? '' : 's'} to look at
          </h3>
          <div className="panel rows">
            {conflicts.map((conflict) => {
              const body = (
                <>
                  <span
                    aria-hidden
                    style={{
                      width: 7,
                      height: 7,
                      flex: 'none',
                      borderRadius: '50%',
                      background:
                        conflict.severity === 'high'
                          ? 'var(--destructive)'
                          : 'var(--accent)',
                    }}
                  />
                  <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{conflict.title}</span>
                    <span className="t-caption">{conflict.detail}</span>
                  </span>
                  {conflict.to && (
                    <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
                  )}
                </>
              )
              return conflict.to ? (
                <Link key={conflict.id} to={conflict.to} className="row" style={ROW}>
                  {body}
                </Link>
              ) : (
                <div key={conflict.id} className="row" style={ROW}>
                  {body}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="panel" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: NAME_W + columns.length * 26 }}>
            <MonthHeader columns={columns} />
            {rows.map((row) => (
              <TimelineRow key={row.artist.id} row={row} columns={columns} />
            ))}
          </div>
        </div>
      </div>

      <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
        <Key colour="var(--accent)" label="Show day" />
        <Key colour="var(--accent-wash)" label="On tour" />
        <Key colour="var(--panel-raised)" label="Off" />
      </div>
    </div>
  )
}

const ROW: React.CSSProperties = { padding: '12px 16px', gap: 12 }

function Key({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="row" style={{ gap: 7 }}>
      <span
        aria-hidden
        style={{ width: 14, height: 10, borderRadius: 3, background: colour }}
      />
      <span className="t-spec-caption faint">{label}</span>
    </span>
  )
}

/**
 * Month boundaries, and day numbers when they'll fit.
 *
 * Without an axis the grid is just coloured squares — you can see there's a
 * run but not when it starts. Day numbers are dropped past a month's range,
 * where they'd collide into a grey smear and the month markers carry it.
 */
function MonthHeader({ columns }: { columns: Date[] }) {
  const showDays = columns.length <= 31
  const todayKey = dayKeyOf(new Date())

  return (
    <div className="stack" style={{ gap: 0, padding: '8px 0 4px' }}>
      <div className="row" style={{ gap: 0 }}>
        <span style={{ width: NAME_W, flex: 'none' }} />
        {columns.map((date, index) => (
          <span key={date.getTime()} style={{ ...TICK, fontWeight: 600 }}>
            {date.getDate() === 1 || index === 0
              ? date.toLocaleDateString('en-GB', { month: 'short' })
              : ''}
          </span>
        ))}
      </div>

      {showDays && (
        <div className="row" style={{ gap: 0 }}>
          <span style={{ width: NAME_W, flex: 'none' }} />
          {columns.map((date) => {
            const isToday = dayKeyOf(date) === todayKey
            return (
              <span
                key={date.getTime()}
                style={{
                  ...TICK,
                  color: isToday ? 'var(--accent-text)' : 'var(--text-tertiary)',
                  fontWeight: isToday ? 700 : 400,
                }}
              >
                {date.getDate()}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

const TICK: React.CSSProperties = {
  width: 26,
  flex: 'none',
  fontSize: 9,
  fontFamily: 'var(--font-spec)',
  color: 'var(--text-tertiary)',
  whiteSpace: 'nowrap',
  textAlign: 'center',
}

function TimelineRow({
  row,
  columns,
}: {
  row: { artist: WithId<Artist>; tours: Array<WithId<Tour>>; shows: Array<WithId<Show>> }
  columns: Date[]
}) {
  const showDays = useMemo(
    () => new Set(row.shows.map((show) => dayKeyOf(show.date.toDate()))),
    [row.shows],
  )
  const todayKey = dayKeyOf(new Date())

  return (
    <div
      className="row"
      style={{ gap: 0, borderTop: 'var(--hairline-width) solid var(--hairline)' }}
    >
      <Link
        to={`/artists/${row.artist.id}`}
        style={{ width: NAME_W, flex: 'none', padding: '10px 16px', minWidth: 0 }}
        className="truncate"
      >
        <span style={{ fontWeight: 600 }}>{row.artist.name}</span>
      </Link>

      {columns.map((date) => {
        const key = dayKeyOf(date)
        const onTour = row.tours.some(
          (tour) =>
            dayKeyOf(tour.startDate.toDate()) <= key && dayKeyOf(tour.endDate.toDate()) >= key,
        )
        const isShow = showDays.has(key)
        return (
          <span
            key={key}
            title={`${row.artist.name} — ${formatDayLong(date)}${isShow ? ' · show' : onTour ? ' · on tour' : ''}`}
            style={{
              width: 26,
              flex: 'none',
              height: 34,
              display: 'grid',
              placeItems: 'center',
              // A single hairline down today's column, so it can be found
              // without counting across from the month label.
              boxShadow:
                key === todayKey
                  ? 'inset 1px 0 0 var(--accent), inset -1px 0 0 var(--accent)'
                  : 'none',
            }}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                // Show days are marked distinctly from travel and off days —
                // a solid block for the gig, a wash for the run around it.
                background: isShow
                  ? 'var(--accent)'
                  : onTour
                    ? 'var(--accent-wash)'
                    : 'var(--panel-raised)',
              }}
            />
          </span>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------- conflicts */

/**
 * The four checks from the spec, in severity order.
 *
 * Every one is something a tour manager would otherwise find out about from a
 * phone call. They are computed rather than stored, so fixing the underlying
 * record makes the warning disappear on its own.
 */
function useConflicts({
  rows,
  crew,
  people,
  startMs,
  days,
}: {
  rows: Array<{ artist: WithId<Artist>; tours: Array<WithId<Tour>>; shows: Array<WithId<Show>> }>
  crew: Array<WithId<TourCrew>>
  people: ReturnType<typeof usePeople>
  startMs: number
  days: number
}): Conflict[] {
  return useMemo(() => {
    const found: Conflict[] = []
    const horizon = startMs + days * DAY

    const tourById = new Map(rows.flatMap((row) => row.tours.map((tour) => [tour.id, row])))

    // 1. The same person on two artists' tours at once — the shared-FOH
    //    problem, and the one that ends with nobody at the desk.
    const byPerson = new Map<string, Array<{ tour: WithId<Tour>; artist: WithId<Artist> }>>()
    for (const member of crew) {
      const identity = member.personId ?? member.contactId
      if (!identity) continue
      const row = tourById.get(member.tourId)
      if (!row) continue
      byPerson.set(identity, [
        ...(byPerson.get(identity) ?? []),
        { tour: row.tours.find((entry) => entry.id === member.tourId)!, artist: row.artist },
      ])
    }

    for (const [personId, bookings] of byPerson) {
      for (let i = 0; i < bookings.length; i += 1) {
        for (let j = i + 1; j < bookings.length; j += 1) {
          const a = bookings[i]
          const b = bookings[j]
          if (a.artist.id === b.artist.id) continue
          if (
            a.tour.startDate.toMillis() > b.tour.endDate.toMillis() ||
            b.tour.startDate.toMillis() > a.tour.endDate.toMillis()
          ) {
            continue
          }
          const name = people.find((person) => person.id === personId)?.name ?? 'Someone'
          found.push({
            id: `double-${personId}-${a.tour.id}-${b.tour.id}`,
            severity: 'high',
            title: `${name} is booked on two tours at once`,
            detail: `${a.artist.name} — ${a.tour.tourName} overlaps ${b.artist.name} — ${b.tour.tourName}.`,
            to: `/tours/${a.tour.id}/crew`,
          })
        }
      }
    }

    // 2. Two artists in the same market inside a fortnight — they compete for
    //    the same audience, and the second one sells worse.
    const byCity = new Map<string, Array<{ show: WithId<Show>; artist: WithId<Artist> }>>()
    for (const row of rows) {
      for (const show of row.shows) {
        if (!show.city) continue
        const key = show.city.toLowerCase()
        byCity.set(key, [...(byCity.get(key) ?? []), { show, artist: row.artist }])
      }
    }
    for (const [, entries] of byCity) {
      for (let i = 0; i < entries.length; i += 1) {
        for (let j = i + 1; j < entries.length; j += 1) {
          const a = entries[i]
          const b = entries[j]
          if (a.artist.id === b.artist.id) continue
          const gap = Math.abs(a.show.date.toMillis() - b.show.date.toMillis())
          if (gap > 14 * DAY) continue
          if (a.show.date.toMillis() < startMs && b.show.date.toMillis() < startMs) continue
          found.push({
            id: `market-${a.show.id}-${b.show.id}`,
            severity: 'medium',
            title: `${a.artist.name} and ${b.artist.name} both play ${a.show.city}`,
            detail: `${formatDayLong(a.show.date)} and ${formatDayLong(b.show.date)} — ${Math.round(gap / DAY)} days apart.`,
            to: `/tours/${a.show.tourId}/shows/${a.show.id}`,
          })
        }
      }
    }

    // 3. A show with nobody on it. Crew get assigned late and then forgotten,
    //    and the tour that notices on the day is the one that pays for it.
    for (const row of rows) {
      const withCrew = new Set(crew.map((member) => member.tourId))
      for (const tour of row.tours) {
        if (withCrew.has(tour.id)) continue
        if (tour.endDate.toMillis() < startMs || tour.startDate.toMillis() > horizon) continue
        found.push({
          id: `nocrew-${tour.id}`,
          severity: 'medium',
          title: `${row.artist.name} — ${tour.tourName} has no crew`,
          detail: `Runs ${formatDayLong(tour.startDate)} to ${formatDayLong(tour.endDate)} with nobody assigned.`,
          to: `/tours/${tour.id}/crew`,
        })
      }
    }

    // 4. A passport expiring within six months of an international date. Most
    //    countries refuse entry on that alone, and it is invisible until the
    //    airport.
    const soon = startMs + 182 * DAY
    for (const person of people) {
      const expiry = person.passport?.expiryDate?.toMillis?.()
      if (!expiry || expiry > soon) continue
      const theirTours = crew
        .filter((member) => (member.personId ?? member.contactId) === person.id)
        .map((member) => tourById.get(member.tourId))
        .filter(Boolean)
      if (theirTours.length === 0) continue
      found.push({
        id: `passport-${person.id}`,
        severity: 'high',
        title: `${person.name}'s passport expires soon`,
        detail: `Expires ${formatDayLong(person.passport!.expiryDate!)}. Most countries refuse entry inside six months.`,
      })
    }

    return found.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1))
  }, [rows, crew, people, startMs, days])
}

/** `YYYY-MM-DD` in local time, for comparing days without timezone drift. */
function dayKeyOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}
